import store from '@/store';
import api, { toGraphQLQuery, getGraphQLHeaders } from '@/api';
import log from '@/configured/logger';
import constants from '@/constants';
import { localeToCraftSiteHandle } from '@/configured/languages/config';
import {
  isBootstrapRequest,
  normalizeBootstrapOptions,
  runSharedLoad,
} from '@/base-data/shared-load';

const exports = {
  namespace: 'navigation',
  module: {
    namespaced: true,
    state: {
      usefulResourceLinks: [],
      utilityLinks: [],
      footerLinks: [],
      socialLinks: [],
      loaded: false,
    },
    mutations: {
      SET_USEFUL_RESOURCES: (state, usefulResourceLinks) => {
        state.usefulResourceLinks = usefulResourceLinks;
      },
      SET_UTILITY: (state, utilityLinks) => {
        state.utilityLinks = utilityLinks;
      },
      SET_FOOTER: (state, footerLinks) => {
        state.footerLinks = footerLinks;
      },
      SET_SOCIAL: (state, socialLinks) => {
        state.socialLinks = socialLinks;
      },
      SET_LOADED: (state, loaded) => {
        state.loaded = loaded;
      },
    },
    actions: {
      loadNavigationDataIfNeeded,
      loadUsefulResources,
      loadUtility,
      loadFooter,
      loadSocial,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

async function loadNavigationDataIfNeeded(
  { state, commit },
  payload = false
) {
  const { forceInline, mode, bootstrapContext } =
    normalizeBootstrapOptions(payload);

  if (state.loaded && !forceInline) {
    if (isBootstrapRequest(bootstrapContext)) {
      return Promise.resolve();
    }

    log.info('loadNavigationDataIfNeeded: optimistic reload');
    store.dispatch(`${exports.namespace}/loadUsefulResources`);
    store.dispatch(`${exports.namespace}/loadUtility`);
    store.dispatch(`${exports.namespace}/loadSocial`);
    store.dispatch(`${exports.namespace}/loadFooter`);
    return Promise.resolve();
  }

  log.info('loadNavigationDataIfNeeded: needed data');
  const language = store.state.i18n.language || 'en';

  return runSharedLoad(
    `${exports.namespace}:${language}`,
    () =>
      Promise.all([
        store.dispatch(`${exports.namespace}/loadUsefulResources`),
        store.dispatch(`${exports.namespace}/loadUtility`),
        store.dispatch(`${exports.namespace}/loadSocial`),
        store.dispatch(`${exports.namespace}/loadFooter`),
      ]).then(() => {
        commit('SET_LOADED', true);
      }),
    {
      bootstrapContext,
      metricKey: exports.namespace,
    }
  );
}

async function loadNavigationMenu(navigationMenuSlug) {
  const site = localeToCraftSiteHandle(store.state.i18n.language);
  const query = `{
    navigationNodes (navHandle: "${navigationMenuSlug}", site: "${site}") {
      title
      url
      classes
      type
    }
  }`;

  return api
    .get(constants.GRAPH_QL_ENDPOINT, {
      params: {
        query: toGraphQLQuery(query),
      },
      headers: getGraphQLHeaders(),
    })
    .then((response) => {
      // If no navigation nodes found for this site, fall back to English
      if (!response.data.navigationNodes || response.data.navigationNodes.length === 0) {
        if (site !== 'en') {
          log.info(`No navigation nodes for ${navigationMenuSlug} in ${site}, falling back to English`);
          return loadNavigationMenuForSite(navigationMenuSlug, 'en');
        }
      }
      return response;
    })
    .catch((error) => {
      if (api.isCancel(error)) {
        log.info(`Caught cancellation`);
        return;
      }
      log.error(`Error loading ${navigationMenuSlug}`, error);
      // Fall back to English on error
      if (site !== 'en') {
        log.info(`Error loading ${navigationMenuSlug} for ${site}, falling back to English`);
        return loadNavigationMenuForSite(navigationMenuSlug, 'en');
      }
      throw error;
    });
}

async function loadNavigationMenuForSite(navigationMenuSlug, site) {
  const query = `{
    navigationNodes (navHandle: "${navigationMenuSlug}", site: "${site}") {
      title
      url
      classes
      type
    }
  }`;

  return api
    .get(constants.GRAPH_QL_ENDPOINT, {
      params: {
        query: toGraphQLQuery(query),
      },
      headers: getGraphQLHeaders(),
    });
}

async function loadUsefulResources({ state, commit }) {
  return loadNavigationMenu('usefulResourcesNav').then((data) => {
    log.info('Loaded useful resources', { data });
    commit('SET_USEFUL_RESOURCES', data.data.navigationNodes);
  });
}

async function loadUtility({ state, commit }) {
  return loadNavigationMenu('utilityNav').then((data) => {
    log.info('Loaded utility nav', { data });
    commit('SET_UTILITY', data.data.navigationNodes);
  });
}

async function loadFooter({ state, commit }) {
  return loadNavigationMenu('footerNav').then((data) => {
    log.info('Loaded footer', { data });
    commit('SET_FOOTER', data.data.navigationNodes);
  });
}

async function loadSocial({ state, commit }) {
  return loadNavigationMenu('socialNav').then((data) => {
    log.info('Loaded social', { data });
    commit('SET_SOCIAL', data.data.navigationNodes);
  });
}
